<template>
  <v-dialog
    v-model="dialog"
    :max-width="400"
    :retain-focus="false"
    @click:outside="closeDialog"
    @keydown.esc="closeDialog"
  >
    <v-card outlined>
      <v-card-title>
        {{ $t('rrule.eventRecurrence') }}
        <p-btn-icon
          type="close"
          btn-class="mr-n3 ml-auto"
          @click="closeDialog"
        ></p-btn-icon>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pt-5 pb-0">
        <v-form ref="form" v-model="valid">
          <v-row dense align="center">
            <v-col cols="auto">
              {{ $t('rrule.repeatEvery') }}
            </v-col>
            <v-col cols="3" class="pl-3">
              <v-text-field
                type="number"
                v-model="rruleOptions.interval"
                :min="1"
                class="p-input-solo-filled"
                hide-details
                solo
                dense
                @change="onIntervalChange"
              ></v-text-field>
            </v-col>
            <v-col cols="4">
              <v-select
                v-model="rruleOptions.freq"
                :items="freqItems"
                :return-object="false"
                class="p-input-solo-filled"
                hide-details
                solo
                dense
              ></v-select>
            </v-col>
          </v-row>

          <div v-if="rruleOptions.freq === freqWeekly">
            <div class="mt-5 mb-3">{{ $t('rrule.repeatOn') }}</div>
            <v-btn-toggle
              v-model="rruleOptions.byweekday"
              :mandatory="rruleOptions.freq === freqWeekly"
              active-class="primary white--text"
              multiple
              dense
            >
              <v-tooltip
                v-for="item of byweekdayItems"
                :key="item.text"
                bottom
              >
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    :value="item.value"
                    min-width="40"
                  >
                    {{ item.text[0] }}
                  </v-btn>
                </template>
                <span>{{ item.text }}</span>
              </v-tooltip>
            </v-btn-toggle>
          </div>

          <div class="mt-6">{{ $t('rrule.ends') }}</div>
          <v-radio-group v-model="ends">
            <v-radio :label="$t('rrule.never')" value="never"></v-radio>
            <v-radio value="until">
              <template v-slot:label>
                <v-row dense align="center">
                  <v-col cols="5">
                    {{ $t('rrule.on') }}
                  </v-col>
                  <v-col cols="5">
                    <p-date-picker
                      v-model="rruleOptions.until"
                      @click="ends = 'until'"
                      input-class="p-input-solo-filled"
                      hide-details
                      dense
                      solo
                    ></p-date-picker>
                  </v-col>
                </v-row>
              </template>
            </v-radio>
            <v-radio value="count">
              <template v-slot:label>
                <v-row dense align="center">
                  <v-col cols="5">
                    {{ $t('rrule.after') }}
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      v-model="rruleOptions.count"
                      type="number"
                      class="p-input-solo-filled"
                      min="1"
                      hide-details
                      solo
                      dense
                      @change="onCountChange"
                    >
                      <template v-slot:append>
                        {{ $t('rrule.occurrences') }}
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>
              </template>
            </v-radio>
          </v-radio-group>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="justify-center">
        <v-btn text @click="closeDialog">{{ $t('cancel') }}</v-btn>
        <v-btn text color="primary" @click="submit">{{ $t('ok') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>


<script>
import moment from 'moment'

import { RRule } from 'rrule'

import PDatePicker from '@/components/PDatePicker'
import PBtnIcon from '@/components/PBtnIcon'

import dialogMixin from '@/mixins/dialog.mixin'


export default {
  components: {
    PDatePicker,
    PBtnIcon
  },


  mixins: [
    dialogMixin
  ],


  props: {
    rrule: String,
    startDate: String
  },


  data () {
    return {
      valid: true,
      rruleOptions: {},
      ends: 'never'
    }
  },


  computed: {
    freqWeekly () {
      return RRule.WEEKLY
    },

    freqItems () {
      return [
        { value: RRule.DAILY, text: this.$t('rrule.day') },
        { value: RRule.WEEKLY, text: this.$t('rrule.week') },
        { value: RRule.MONTHLY, text: this.$t('rrule.month') },
        { value: RRule.YEARLY, text: this.$t('rrule.year') }
      ]
    },

    byweekdayItems () {
      return [
        { value: RRule.MO, text: this.$t('week.monday') },
        { value: RRule.TU, text: this.$t('week.tuesday') },
        { value: RRule.WE, text: this.$t('week.wednesday') },
        { value: RRule.TH, text: this.$t('week.thursday') },
        { value: RRule.FR, text: this.$t('week.friday') },
        { value: RRule.SA, text: this.$t('week.saturday') },
        { value: RRule.SU, text: this.$t('week.sunday') }
      ]
    }
  },


  methods: {
    onDialogOpen () {
      const options = {}

      if (this.rrule) {
        const origOptions = RRule.fromString(this.rrule).origOptions
        options.freq = origOptions.freq
        options.byweekday = origOptions.byweekday
        options.count = origOptions.count
        options.interval = origOptions.interval
        if (origOptions.until) {
          options.until = moment(origOptions.until).format('YYYY-MM-DD')
        }
      } else {
        options.freq = RRule.WEEKLY
        options.interval = 1
      }

      if (!options.until && this.startDate) {
        options.until = moment(this.startDate).add(1, 'M').format('YYYY-MM-DD')
      }
      if (!options.byweekday && this.startDate) {
        options.byweekday = [this.getRRuleWeekDay(moment(this.startDate).weekday())]
      }
      if (!options.count) {
        options.count = 10
      }

      this.rruleOptions = options
    },

    getRRuleWeekDay (weekday) {
      switch (weekday) {
        case 1: return RRule.MO
        case 2: return RRule.TH
        case 3: return RRule.WE
        case 4: return RRule.TH
        case 5: return RRule.FR
        case 6: return RRule.SA
        default: return RRule.SU
      }
    },

    onCountChange () {
      if (!this.rruleOptions.count || this.rruleOptions.count < 1) {
        this.rruleOptions.count = 1
      }
    },

    onIntervalChange () {
      if (!this.rruleOptions.interval || this.rruleOptions.interval < 1) {
        this.rruleOptions.interval = 1
      }
    },

    submit () {
      const options = {
        freq: this.rruleOptions.freq,
        interval: this.rruleOptions.interval,
        dtstart: new Date(this.startDate)
      }

      if (options.freq === RRule.WEEKLY) {
        options.byweekday = this.rruleOptions.byweekday
      }

      switch (this.ends) {
        case 'count':
          options.count = this.rruleOptions.count
          break
        case 'until':
          options.until = new Date(this.rruleOptions.until)
          break
      }

      const newRRule = new RRule(options).toString()
      this.$emit('update:rrule', newRRule)
      this.closeDialog()
    }
  }
}
</script>
